import {Injectable} from '@angular/core';
import {DataService} from "../../../../services/external-api/data.service";
import {Observable} from "rxjs";
import {emptyBrRule, emptyBrTag, emptyBrType, IBrRule, IBrTag, IBrType} from "@retail-data-factory/rdf-angular-types-api";
import {map} from "rxjs/operators";
import {ConfigurationManager} from "../../../../store/configuration-manager/configuration-manager";
import {IServiceResponse} from "../../../../services/external-api/response-interfaces";

@Injectable({
  providedIn: 'root'
})
export class ApiBusinessRulesService {

  ruleUrl = 'apiBusinessRulesUrl';
  ruleEngineUrl = 'apiBRUrl';

  constructor(
    private dataService: DataService,
    private configurationManager: ConfigurationManager
  ) {
  }

  getRules(): Observable<Array<IBrRule>> {
    return this.dataService.getReportData(this.configurationManager.getApiUrl(this.ruleUrl) + '/rules', {}).pipe(
      map(result => {
          const rules: IBrRule[] = result.report.map(item => this.mapRuleFromService(item));
          return rules;
        }
      )
    );
  }

  getTags(): Observable<Array<IBrTag>> {
    return this.dataService.getReportData(this.configurationManager.getApiUrl(this.ruleUrl) + '/tags', {}).pipe(
      map(result => {
          const tags: IBrTag[] = result.report.map(item => this.mapTagFromService(item));
          return tags;
        }
      )
    );
  }

  getTypes(): Observable<Array<IBrType>> {
    return this.dataService.getReportData(this.configurationManager.getApiUrl(this.ruleUrl) + '/types', {}).pipe(
      map(result => {
          const types: IBrType[] = result.report.map(item => this.mapTypeFromService(item));
          return types;
        }
      )
    );
  }

  getRule(code: string): Observable<IBrRule> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl(this.ruleUrl)}/rules/${code}`, {}).pipe(
      map(result => {
          if (result.report.length > 0) {
            return this.mapRuleFromService(result.report[0]);
          }
          else {
            return emptyBrRule;
          }
        }
      )
    );
  }

  getTag(code: string): Observable<IBrTag> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl(this.ruleUrl)}/tags/${code}`, {}).pipe(
      map(result => {
          if (result.report.length > 0) {
            return this.mapTagFromService(result.report[0]);
          }
          else {
            return emptyBrTag;
          }
        }
      )
    );
  }

  getType(code: string): Observable<IBrType> {
    return this.dataService.getReportData(`${this.configurationManager.getApiUrl(this.ruleUrl)}/types/${code}`, {}).pipe(
      map(result => {
          if (result.report.length > 0) {
            return this.mapTypeFromService(result.report[0]);
          }
          else {
            return emptyBrType;
          }
        }
      )
    );
  }

  createRule(itm: IBrRule): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl(this.ruleUrl)}/rules`, {}, this.createRuleBody(itm));
  }

  createTag(itm: IBrTag): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl(this.ruleUrl)}/tags`, {}, itm);
  }

  createType(itm: IBrType): Observable<IServiceResponse> {
    return this.dataService.postData(`${this.configurationManager.getApiUrl(this.ruleUrl)}/types`, {}, itm);
  }

  updateRule(itm: IBrRule): Observable<IServiceResponse> {
    return this.dataService.putData(`${this.configurationManager.getApiUrl(this.ruleUrl)}/rules/${itm.code}`, {}, this.createRuleBody(itm));
  }
  async updateRuleAsync(itm: IBrRule):Promise<IServiceResponse> {
    return this.dataService.putData(`${this.configurationManager.getApiUrl(this.ruleUrl)}/rules/${itm.code}`, {}, this.createRuleBody(itm)).toPromise();
  }

  updateTag(itm: IBrTag): Observable<IServiceResponse> {
    return this.dataService.putData(`${this.configurationManager.getApiUrl(this.ruleUrl)}/tags/${itm['tag_code']}`, {}, itm);
  }

  updateType(itm: IBrType): Observable<IServiceResponse> {
    return this.dataService.putData(`${this.configurationManager.getApiUrl(this.ruleUrl)}/types/${itm.code}`, {}, itm);
  }

  deleteRule(code: string): Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl(this.ruleUrl)}/rules/${code}`, {});
  }

  deleteTag(code: string): Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl(this.ruleUrl)}/tags/${code}`, {});
  }

  deleteType(code: string): Observable<IServiceResponse> {
    return this.dataService.deleteData(`${this.configurationManager.getApiUrl(this.ruleUrl)}/types/${code}`, {});
  }


  executeRule(ruleCode: string, header: object, body: object, withLog = false) {
    return this.dataService.postData(`${this.configurationManager.getApiUrl(this.ruleEngineUrl)}/rules/${ruleCode}/?action=execute&log=${withLog}`, header, body);

  }

  mapRuleFromService(item: object): IBrRule {
    const result: IBrRule = {
      code: item['br_code'],
      name: item['br_name'],
      typeCode: item['br_type_code'],
      typeName: item['br_type_name'],
      statusId: item['br_status_id'],
      tags: item['br_tags'].filter(t => t.tag_code !== null),
      brText: item['br_text'],
      description: item['br_description'],
      profile: item['profile'],
      defaultHeader: item['profile'] && item['profile']['default_header']?item['profile']['default_header']:{},
      defaultBody: item['profile'] && item['profile']['default_body']?item['profile']['default_body']:{}
    };
    return result;
  }

  mapTagFromService(item: object): IBrTag {
    const result: IBrTag = {
      tag_code: item['tag_code'],
      tag_name: item['tag_name']
    };
    return result;
  }

  mapTypeFromService(item: object): IBrType {
    const result: IBrType = {
      code: item['br_type_code'],
      name: item['br_type_name']
    };
    return result;
  }

  createRuleBody(rule: IBrRule): any {
    const result = {
      br_code: rule.code,
      br_name: rule.name,
      br_type_code: rule.typeCode,
      br_status: rule.statusId,
      br_tags: rule.tags.map(i => { return {'tag_code': i.tag_code, 'tag_name': i.tag_name} }),
      br_text: rule.brText,
      br_description: rule.description,
      profile: {
        ...rule.profile,
        default_header: rule.defaultHeader,
        default_body: rule.defaultBody,
      }
    };
    return result;

  }
}
